import {
  Button,
  FormField,
  TextInput,
  Avatar,
  AutocompleteNoOptions,
  DropdownItem,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import {
  AutocompleteMultipleSearch,
  AutocompleteSearch,
} from 'src/core/common/components/AutocompleteSearch';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { validateCostCenterForm } from './validate';
import { useOrganisationCompanyList } from '../../../../hooks/data/useOrganisationCompanyList';
import { useOrganisationMembers } from '../../hooks/useOrganisationMembers';
import { useOrganisationSettings } from '../../hooks/useOrganisationSettings';
import { type Setting } from '../../setting';

type MemberOption = {
  key: string;
  label: string;
  avatar?: string | null;
};

type Props = {
  onComplete(values: FormValues): void;
  onCancel(): void;
  settings: Setting[];
};

export type FormValues = {
  name: string;
  ownerId: string;
  viewerIds: string[];
  targetCompanyIds: string[];
  entitySpecificIds: {
    costCenterId: string;
    companyId: string;
  }[];
  crossEntitySettingsIds: Setting['id'][];
};

export const CostCenterMergeContent = ({
  onComplete,
  onCancel,
  settings,
}: Props) => {
  const { t } = useTranslation('global');
  const membersQueryState = useOrganisationMembers();
  const companies = useOrganisationCompanyList();
  const organisationSettingsQueryState = useOrganisationSettings();
  const members = unwrapQuery(membersQueryState);
  const reshapedMembers: MemberOption[] =
    members?.map((member) => ({
      key: member.id,
      label: member.isPending ? member.email : member.fullname || member.email,
      avatar: member.avatar,
    })) ?? [];

  const getValueIfSame = <K extends keyof Setting>(
    key: K,
  ): Setting[K] | undefined => {
    const allSame = new Set(settings.map((s) => s[key]?.toString())).size === 1;
    return allSame ? settings[0][key] : undefined;
  };

  const initialCompanyIds = useMemo(
    () => settings.flatMap((setting) => setting.companyIds),
    [settings],
  );

  const selectedEntities = useMemo(
    () => settings?.filter((c) => c.type === 'entity-specific') ?? [],
    [settings],
  );

  const selectedSettings = useMemo(
    () => settings?.filter((c) => c.type === 'cross-entity') ?? [],
    [settings],
  );

  const formikProps = useFormik<FormValues>({
    enableReinitialize: false,
    initialValues: {
      name: getValueIfSame('name') ?? '',
      ownerId: getValueIfSame('ownerId') ?? '',
      viewerIds: getValueIfSame('viewerIds') ?? [],
      targetCompanyIds: initialCompanyIds,
      entitySpecificIds: selectedEntities.flatMap((c) => c.entities ?? []),
      crossEntitySettingsIds: selectedSettings.map((c) => c?.id),
    },
    validate: (values) =>
      validateCostCenterForm(
        values,
        t,
        organisationSettingsQueryState.data,
        settings,
      ),
    onSubmit: async (values) => {
      onComplete(values);
    },
  });

  const selectedOwner = reshapedMembers.find(
    (member) => member.key === formikProps.values.ownerId,
  );

  const companyOptions = companies.map((company) => ({
    key: company.id,
    label: company.name,
  }));

  const selectedOptions = companyOptions.filter((option) =>
    formikProps.values.targetCompanyIds.includes(option.key),
  );

  return (
    <>
      <FormField
        label={t('costCenters.form.name')}
        alertMessage={
          formikProps.errors.name && formikProps.touched.name
            ? formikProps.errors.name
            : undefined
        }
      >
        <TextInput
          name="name"
          value={formikProps.values.name}
          placeholder={t('costCenters.form.namePlaceholder')}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
      </FormField>
      <FormField
        label={t('multiEntityHub.costCenters.modal.combination.entities')}
        alertMessage={
          formikProps.errors.targetCompanyIds &&
          formikProps.touched.targetCompanyIds
            ? (formikProps.errors.targetCompanyIds as string)
            : (undefined as undefined)
        }
      >
        <AutocompleteMultipleSearch
          fit="parent"
          placeholder={t(
            'multiEntityHub.costCenters.modal.combination.selectEntities',
          )}
          isInvalid={Boolean(
            formikProps.touched.targetCompanyIds &&
              formikProps.errors.targetCompanyIds,
          )}
          options={companyOptions}
          values={selectedOptions}
          onSelect={(options) => {
            formikProps.setFieldValue(
              'companyIds',
              options.map((option) => option.key),
            );
          }}
          translations={{
            selectAll: t(
              'multiEntityHub.costCenters.modal.combination.actions.selectAll',
            ),
            selected: selectedOptions
              .map(
                (option) =>
                  companies.find((company) => company.id === option.key)?.name,
              )
              .join(', '),
          }}
        />
      </FormField>
      <FormField
        label={t('costCenters.form.owner')}
        alertMessage={
          formikProps.errors.ownerId && formikProps.touched.ownerId
            ? formikProps.errors.ownerId
            : undefined
        }
      >
        <AutocompleteSearch
          name="ownerId"
          fit="parent"
          options={reshapedMembers}
          value={selectedOwner}
          placeholder={t('costCenters.form.owner')}
          isInvalid={Boolean(
            formikProps.errors.ownerId && formikProps.touched.ownerId,
          )}
          renderPrefix={(option) =>
            option && (
              <Avatar
                size={24}
                src={option.avatar ?? undefined}
                text={option.label}
              />
            )
          }
          renderOption={(option, state) => (
            <DropdownItem
              {...state}
              key={option.key}
              label={option.label}
              prefix={
                <Avatar
                  size={24}
                  src={option.avatar ?? undefined}
                  text={option.label}
                />
              }
            />
          )}
          onSelect={(owner) => {
            if (owner) {
              formikProps.setFieldValue('ownerId', owner.key);
              formikProps.setFieldTouched('ownerId', true, false);
            }
          }}
          renderNoOptions={(rawValue) => (
            <AutocompleteNoOptions className="text-center body-m">
              <div>
                <Trans
                  i18nKey="misc.noOptions"
                  values={{ account: rawValue }}
                  components={[
                    <span key="complementary" className="text-content-primary" />,
                  ]}
                />
              </div>
            </AutocompleteNoOptions>
          )}
        />
      </FormField>
      <ViewersField
        viewerOptions={reshapedMembers.filter(
          (member) => member.key !== selectedOwner?.key,
        )}
        selectedViewerIds={formikProps.values.viewerIds}
        onSelectViewers={(viewerIds) =>
          formikProps.setFieldValue('viewerIds', viewerIds)
        }
      />
      {getModalActions({
        onComplete: () => formikProps.submitForm(),
        onCancel,
        translator: t,
      })}
    </>
  );
};

const ViewersField = ({
  viewerOptions,
  selectedViewerIds,
  onSelectViewers,
}: {
  viewerOptions: MemberOption[];
  selectedViewerIds: string[];
  onSelectViewers: (viewerIds: string[]) => void;
}) => {
  const { t } = useTranslation('global');
  const [sortedViewerOptions, setSortedViewerOptions] =
    useState<MemberOption[]>(viewerOptions);

  useEffect(() => {
    setSortedViewerOptions(
      sortViewersBySelected(viewerOptions, selectedViewerIds),
    );
  }, [viewerOptions, selectedViewerIds]);

  return (
    <FormField
      label={t('costCenters.form.viewers')}
      infoTipContent={t('costCenters.form.viewersInfoText')}
    >
      <AutocompleteMultipleSearch
        name="viewerIds"
        fit="parent"
        options={sortedViewerOptions}
        values={viewerOptions.filter((option) =>
          selectedViewerIds.includes(option.key),
        )}
        placeholder={t('costCenters.form.viewersPlaceholder')}
        translations={{
          selected: t('costCenters.form.selectedMember', {
            count: selectedViewerIds.length,
          }),
        }}
        onSelect={(viewers) =>
          onSelectViewers(viewers.map((viewer) => viewer.key))
        }
        onFocus={() => {
          setSortedViewerOptions(
            sortViewersBySelected(viewerOptions, selectedViewerIds),
          );
        }}
      />
    </FormField>
  );
};

const sortViewersBySelected = (
  memberOptions: MemberOption[],
  viewerIds: string[],
) => {
  return [...memberOptions].sort((a) => {
    if (viewerIds.includes(a.key)) {
      return -1;
    }
    return 1;
  });
};

const getModalActions = ({
  onComplete,
  onCancel,
  translator,
}: {
  onComplete: () => void;
  onCancel: () => void;
  translator: TGlobalFunctionTyped;
}) => {
  return (
    <div className="-mb-24 mt-16 flex w-full justify-center gap-16">
      <Button
        key="back"
        variant="secondaryNeutral"
        text={translator(
          'multiEntityHub.costCenters.modal.combination.actions.cancel',
        )}
        onClick={onCancel}
      />
      <Button
        key="submit"
        variant="primaryBrand"
        text={translator(
          'multiEntityHub.costCenters.modal.combination.actions.confirm',
        )}
        onClick={onComplete}
      />
    </div>
  );
};
